/**
 * ProfitLossChart Component - Phase 5 Implementation
 * Bar chart for income vs expenses comparison
 * Shows P&L breakdown with margin analysis
 */

import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Legend,
  ReferenceLine
} from 'recharts';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig
} from '../../ui/chart';
import { Skeleton } from '../../ui/skeleton';
import { TrendingUp, TrendingDown, DollarSign, Percent } from 'lucide-react';
import { cn } from '../../../lib/utils';

interface ProfitLossChartProps {
  data: any[];
  isLoading?: boolean;
  error?: string;
  className?: string;
}

const chartConfig = {
  revenue: {
    label: "Revenue",
    color: "hsl(var(--chart-3))",
  },
  expenses: {
    label: "Expenses",
    color: "hsl(var(--chart-4))",
  },
  netIncome: {
    label: "Net Income",
    color: "hsl(var(--chart-1))",
  },
} satisfies ChartConfig;

// Mock data for demonstration
const generateMockData = () => [
  { category: 'Q1 2024', revenue: 450000, expenses: 320000, netIncome: 130000, margin: 28.9 },
  { category: 'Q2 2024', revenue: 520000, expenses: 360000, netIncome: 160000, margin: 30.8 },
  { category: 'Q3 2024', revenue: 480000, expenses: 340000, netIncome: 140000, margin: 29.2 },
  { category: 'Q4 2024', revenue: 610000, expenses: 380000, netIncome: 230000, margin: 37.7 },
  { category: 'Sales', revenue: 1200000, expenses: 0, netIncome: 1200000, margin: 100 },
  { category: 'Cost of Goods', revenue: 0, expenses: 480000, netIncome: -480000, margin: -40 },
  { category: 'Marketing', revenue: 0, expenses: 120000, netIncome: -120000, margin: -10 },
  { category: 'Operations', revenue: 0, expenses: 150000, netIncome: -150000, margin: -12.5 },
  { category: 'Admin', revenue: 0, expenses: 80000, netIncome: -80000, margin: -6.7 },
  { category: 'R&D', revenue: 0, expenses: 100000, netIncome: -100000, margin: -8.3 },
];

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(value));
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload;
    return (
      <div className="bg-background border rounded-lg shadow-lg p-3 min-w-[220px]">
        <p className="font-medium mb-2">{label}</p>
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center justify-between text-sm mb-1">
            <div className="flex items-center space-x-2">
              <div 
                className="w-3 h-3 rounded"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-muted-foreground">{entry.name}:</span>
            </div>
            <span className="font-medium">
              {formatCurrency(entry.value)}
            </span>
          </div>
        ))}
        {data?.margin && (
          <div className="flex items-center justify-between text-sm pt-2 border-t mt-2">
            <span className="text-muted-foreground">Margin:</span>
            <span className={cn(
              "font-medium",
              data.margin > 0 ? "text-green-600" : "text-red-600"
            )}>
              {data.margin.toFixed(1)}%
            </span>
          </div>
        )}
      </div>
    );
  }
  return null;
};

export const ProfitLossChart: React.FC<ProfitLossChartProps> = ({
  data,
  isLoading = false,
  error,
  className
}) => {
  // Use mock data if no data provided or in loading state
  const chartData = data.length > 0 ? data : generateMockData();

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-2">
          <p className="text-destructive font-medium">Failed to load chart</p>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <Skeleton className="h-6 w-32" />
          <div className="flex space-x-4">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-20" />
          </div>
        </div>
        <Skeleton className="h-64 w-full" />
        <div className="flex space-x-6 justify-center">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
        </div>
      </div>
    );
  }

  // Calculate summary metrics
  const totalRevenue = chartData.reduce((sum, item) => sum + (item.revenue || 0), 0);
  const totalExpenses = chartData.reduce((sum, item) => sum + (item.expenses || 0), 0);
  const netIncome = totalRevenue - totalExpenses;
  const profitMargin = totalRevenue > 0 ? (netIncome / totalRevenue) * 100 : 0;

  return (
    <div className={cn("space-y-4", className)}>
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-green-50 dark:bg-green-950/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-700 dark:text-green-300">Total Revenue</p>
              <p className="text-xl font-bold text-green-800 dark:text-green-200">
                {formatCurrency(totalRevenue)}
              </p>
            </div>
            <div className="text-green-600">
              <TrendingUp className="h-6 w-6" />
            </div>
          </div>
        </div>
        
        <div className="bg-red-50 dark:bg-red-950/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-700 dark:text-red-300">Total Expenses</p>
              <p className="text-xl font-bold text-red-800 dark:text-red-200">
                {formatCurrency(totalExpenses)}
              </p>
            </div>
            <div className="text-red-600">
              <TrendingDown className="h-6 w-6" />
            </div>
          </div>
        </div>
        
        <div className={cn(
          "rounded-lg p-4 border",
          netIncome >= 0 
            ? "bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800" 
            : "bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800"
        )}>
          <div className="flex items-center justify-between">
            <div>
              <p className={cn(
                "text-sm",
                netIncome >= 0 
                  ? "text-blue-700 dark:text-blue-300" 
                  : "text-orange-700 dark:text-orange-300"
              )}>
                Net Income
              </p>
              <p className={cn(
                "text-xl font-bold",
                netIncome >= 0 
                  ? "text-blue-800 dark:text-blue-200" 
                  : "text-orange-800 dark:text-orange-200"
              )}>
                {formatCurrency(netIncome)}
              </p>
            </div>
            <div className={cn(
              netIncome >= 0 ? "text-blue-600" : "text-orange-600"
            )}>
              <DollarSign className="h-6 w-6" />
            </div>
          </div>
        </div>
        
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Profit Margin</p>
              <p className={cn(
                "text-xl font-bold",
                profitMargin >= 0 ? "text-green-600" : "text-red-600"
              )}>
                {profitMargin.toFixed(1)}%
              </p>
            </div>
            <div className="text-muted-foreground">
              <Percent className="h-6 w-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Profit & Loss Chart */}
      <ChartContainer config={chartConfig} className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 60,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis 
              dataKey="category" 
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<ChartLegendContent />} />
            
            <ReferenceLine y={0} stroke="hsl(var(--border))" strokeDasharray="2 2" />
            
            <Bar
              dataKey="revenue"
              fill="var(--color-revenue)"
              name="Revenue"
              radius={[2, 2, 0, 0]}
            />
            <Bar
              dataKey="expenses"
              fill="var(--color-expenses)"
              name="Expenses"
              radius={[2, 2, 0, 0]}
            />
            <Bar
              dataKey="netIncome"
              fill="var(--color-netIncome)"
              name="Net Income"
              radius={[2, 2, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartContainer>
    </div>
  );
};